import { useEffect, useCallback, useState, useRef } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { transcriptPath } from '../routes';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Card,
  CardContent,
  Skeleton,
  IconButton,
  Divider,
  Button,
  Tooltip,
  Snackbar,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  Collapse,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { format, isSameDay } from 'date-fns';

// LogRocket logo icon component
const LogRocketIcon = ({ size = 16 }: { size?: number }) => (
  <img
    src="/logrocket-logo.png"
    alt="LogRocket"
    width={size}
    height={size}
    style={{ display: 'block', borderRadius: 2 }}
  />
);

const LOGROCKET_ORG = '6lkodz';
const LOGROCKET_APP = 'level-app';

/**
 * Build a LogRocket session URL for a user around a specific timestamp.
 * Shows sessions in a 4-hour window centered on the request time.
 */
const buildLogRocketUrl = (userId: string, timestamp: string): string => {
  // Ensure timestamp is treated as UTC if no timezone specified
  const normalizedTimestamp = timestamp.endsWith('Z') || timestamp.includes('+')
    ? timestamp
    : timestamp + 'Z';
  const requestTime = new Date(normalizedTimestamp).getTime();
  const twoHours = 2 * 60 * 60 * 1000;
  const newestCursor = requestTime + twoHours; // End 2 hours after request
  const timeRange = 4 * 60 * 60 * 1000; // 4-hour window (2 before, 2 after)
  return `https://app.logrocket.com/${LOGROCKET_ORG}/${LOGROCKET_APP}/sessions?u=${encodeURIComponent(userId)}&newestCursor=${newestCursor}&timeRange=${timeRange}`;
};
import { useRequestStore } from '../stores/requestStore';
import { useFilterStore } from '../stores/filterStore';
import { useExclusionsStore } from '../stores/exclusionsStore';
import ResizableDrawer from '../components/ResizableDrawer';
import { surfaceColor, surfaceLabel } from '../utils/agentSurfaces';
import ForumIcon from '@mui/icons-material/Forum';
import { listRequests, getRequestDetail, getFilterOptions, getAgentNames, getStatsSummary, getTimeseries } from '../api/dashboard';
import { REQUEST_TYPE_COLORS } from '../theme';
import type { RequestSummary, RequestType, StatsSummary, TimeSeriesPoint } from '../types';
import CompactFilters from '../components/filters/CompactFilters';
import TurnKindChip from '../components/agents/TurnKindChip';
import LogRocketHighlightsModal from '../components/LogRocketHighlightsModal';
import QueryAnswerModal from '../components/QueryAnswerModal';
import DailyTrendsChart from '../components/DailyTrendsChart';

const REQUEST_TYPE_LABELS: Record<RequestType, string> = {
  workflow: 'Workflow',
  workflow_plan: 'Plan',
  query: 'Query',
  agent: 'Agent',
  checklist: 'Checklist',
  compare: 'Compare',
  other: 'Other',
};

export default function RequestsExplorer() {
  const {
    requests,
    selectedRequest,
    isDetailOpen,
    dateRange,
    totalRequests,
    isLoading,
    isLoadingDetail,
    setRequests,
    setSelectedRequest,
    setDetailOpen,
    goToPreviousDay,
    setLoading,
    setLoadingDetail,
  } = useRequestStore();

  const { filters, isOptionsLoaded, setSearch, setAvailableOptions, setAvailableAgentNames, getFiltersForApi } = useFilterStore();
  const { excludedOrgIds, effectiveExcludedUserIds, isInitialized: exclusionsInitialized } = useExclusionsStore();
  const navigate = useNavigate();

  const [copySnackbar, setCopySnackbar] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });


  // Stats state
  const [stats, setStats] = useState<StatsSummary | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Daily trends state
  const [trendsExpanded, setTrendsExpanded] = useState(false);
  const [trendsData, setTrendsData] = useState<TimeSeriesPoint[] | null>(null);
  const [trendsLoading, setTrendsLoading] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(1);

  // LogRocket highlights modal state
  const [highlightsModal, setHighlightsModal] = useState<{
    open: boolean;
    userId: string | null;
    timestamp: string | null;
    userName: string | null;
  }>({
    open: false,
    userId: null,
    timestamp: null,
    userName: null,
  });

  const openHighlightsModal = (userId: string, timestamp: string, userName: string | null) => {
    setHighlightsModal({ open: true, userId, timestamp, userName });
  };

  const closeHighlightsModal = () => {
    setHighlightsModal({ open: false, userId: null, timestamp: null, userName: null });
  };

  // Query answer modal state
  const [answerModal, setAnswerModal] = useState<{
    open: boolean;
    traceId: string | null;
    question: string | null;
  }>({
    open: false,
    traceId: null,
    question: null,
  });

  const openAnswerModal = (traceId: string, question: string | null) => {
    setAnswerModal({ open: true, traceId, question });
  };

  const closeAnswerModal = () => {
    setAnswerModal({ open: false, traceId: null, question: null });
  };

  // Track if initial load has completed to prevent duplicate fetches
  const isInitializedRef = useRef(false);
  const isFetchingRef = useRef(false);

  const handleCopy = useCallback(async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySnackbar({ open: true, message: `${label} copied!` });
    } catch {
      setCopySnackbar({ open: true, message: 'Failed to copy' });
    }
  }, []);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const exclOrgs = excludedOrgIds.length > 0 ? excludedOrgIds : undefined;
      const exclUsers = effectiveExcludedUserIds.length > 0 ? effectiveExcludedUserIds : undefined;
      const filtersForApi = getFiltersForApi();
      const statsData = await getStatsSummary(
        {
          ...filtersForApi,
          startDate: dateRange.start,
          endDate: dateRange.end,
        },
        exclOrgs,
        exclUsers
      );
      setStats(statsData);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setStatsLoading(false);
    }
  }, [dateRange, getFiltersForApi, excludedOrgIds, effectiveExcludedUserIds]);

  const fetchTrendsData = useCallback(async () => {
    if (trendsLoading) return;

    setTrendsLoading(true);
    try {
      const exclOrgs = excludedOrgIds.length > 0 ? excludedOrgIds : undefined;
      const exclUsers = effectiveExcludedUserIds.length > 0 ? effectiveExcludedUserIds : undefined;
      const filtersForApi = getFiltersForApi();

      // Determine granularity based on date range
      const daysDiff = Math.ceil(
        (dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24)
      );
      const granularity = daysDiff > 90 ? 'week' : 'day';

      const data = await getTimeseries(
        { ...filtersForApi, startDate: dateRange.start, endDate: dateRange.end },
        granularity,
        exclOrgs,
        exclUsers
      );
      setTrendsData(data);
    } catch (error) {
      console.error('Failed to fetch trends data:', error);
    } finally {
      setTrendsLoading(false);
    }
  }, [dateRange, getFiltersForApi, excludedOrgIds, effectiveExcludedUserIds, trendsLoading]);

  const handleTrendsToggle = useCallback(() => {
    const newExpanded = !trendsExpanded;
    setTrendsExpanded(newExpanded);
    // Fetch data on first expand or if data was cleared
    if (newExpanded && trendsData === null) {
      fetchTrendsData();
    }
  }, [trendsExpanded, trendsData, fetchTrendsData]);

  const fetchRequests = useCallback(async (page: number = currentPage) => {
    // Prevent concurrent fetches
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    setLoading(true);
    try {
      const exclOrgs = excludedOrgIds.length > 0 ? excludedOrgIds : undefined;
      const exclUsers = effectiveExcludedUserIds.length > 0 ? effectiveExcludedUserIds : undefined;
      const response = await listRequests(
        dateRange.start,
        dateRange.end,
        page,
        pageSize,
        'desc',
        getFiltersForApi(),
        exclOrgs,
        exclUsers
      );
      setRequests(response.requests, response.total);
      setTotalPages(response.totalPages);
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [dateRange, getFiltersForApi, excludedOrgIds, effectiveExcludedUserIds, setLoading, setRequests, currentPage, pageSize]);

  // Initial load: wait for exclusions to be initialized, then fetch requests (once)
  useEffect(() => {
    if (isInitializedRef.current) return;
    // Wait for exclusions to be initialized (by Header) before fetching
    if (!exclusionsInitialized) return;

    const initialize = async () => {
      // Only fetch filter options if not already loaded (e.g., by AuthGuard)
      if (!isOptionsLoaded) {
        try {
          const options = await getFilterOptions();
          setAvailableOptions(options);
        } catch (error) {
          console.error('Failed to fetch filter options:', error);
        }
      }
      try {
        setAvailableAgentNames(await getAgentNames());
      } catch (error) {
        console.error('Failed to fetch agent names:', error);
      }
      // Fetch requests and stats in parallel
      await Promise.all([fetchRequests(), fetchStats()]);
      // Mark as initialized AFTER the first fetch completes
      isInitializedRef.current = true;
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exclusionsInitialized]); // Run when exclusions become initialized

  // Refetch when filters change (after initial load) - reset to page 1
  useEffect(() => {
    if (!isInitializedRef.current) return; // Skip during initial load
    setCurrentPage(1);
    fetchRequests(1);
    fetchStats();
    // Clear trends cache so it refetches on next expand
    setTrendsData(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange, filters.requestTypes, filters.orgIds, filters.userIds, filters.tags, filters.feedbackTypes, filters.checklistOnly, filters.surfaces, filters.agentNames, filters.excludeScheduled, excludedOrgIds, effectiveExcludedUserIds, pageSize]);

  // Refetch trends if expanded and data was cleared (due to filter change)
  useEffect(() => {
    if (trendsExpanded && trendsData === null && !trendsLoading) {
      fetchTrendsData();
    }
  }, [trendsExpanded, trendsData, trendsLoading, fetchTrendsData]);

  // Handle page change
  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    fetchRequests(page);
  };

  // Handle page size change
  const handlePageSizeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newSize = event.target.value as number;
    setPageSize(newSize);
    setCurrentPage(1);
    // fetchRequests will be triggered by the useEffect above due to pageSize dependency
  };

  const handleRequestClick = async (request: RequestSummary) => {
    setDetailOpen(true);
    setLoadingDetail(true);
    try {
      const detail = await getRequestDetail(request.traceId);
      setSelectedRequest(detail);
    } catch (error) {
      console.error('Failed to fetch request detail:', error);
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      fetchRequests();
    }
  };

  const isSingleDay = isSameDay(dateRange.start, dateRange.end);

  const formatRangeLabel = () => {
    if (isSingleDay) {
      return format(dateRange.start, 'EEE, MMM d');
    }
    return `${format(dateRange.start, 'MMM d')} - ${format(dateRange.end, 'MMM d')}`;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      {/* Compact Filters Row - Fixed */}
      <Box sx={{ flexShrink: 0 }}>
        <CompactFilters />
      </Box>

      {/* Search + Stats Row - Combined */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 0.75,
          px: 1.5,
          mb: 1.5,
          borderRadius: 1,
          bgcolor: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}
      >
        {/* Search */}
        <TextField
          placeholder="Search request text or trace ID..."
          value={filters.search}
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
          size="small"
          sx={{ width: 260, '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" sx={{ fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
        />

        {/* Stats - Centered */}
        {statsLoading || !stats ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, justifyContent: 'center' }}>
            <Skeleton variant="text" width={80} height={24} />
            <Skeleton variant="text" width={60} height={24} />
            <Skeleton variant="text" width={60} height={24} />
            <Skeleton variant="text" width={70} height={24} />
            <Skeleton variant="text" width={50} height={24} />
            <Skeleton variant="text" width={50} height={24} />
            <Skeleton variant="text" width={80} height={24} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, justifyContent: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
              {stats?.totalRequests.toLocaleString() ?? totalRequests.toLocaleString()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              requests
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            for {formatRangeLabel()}
          </Typography>
          {stats && (
            <>
              <Typography color="text.disabled" sx={{ fontSize: '0.75rem' }}>|</Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {stats.totalWorkflows.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  workflows
                </Typography>
              </Box>
              <Typography color="text.disabled" sx={{ fontSize: '0.75rem' }}>|</Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {stats.totalQueries.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  queries
                </Typography>
                {stats.checklistQueries > 0 && (
                  <Typography variant="caption" sx={{ color: '#14b8a6', fontSize: '0.7rem' }}>
                    ({stats.checklistQueries} checklist)
                  </Typography>
                )}
              </Box>
              {stats.totalCompare > 0 && (
                <>
                  <Typography color="text.disabled" sx={{ fontSize: '0.75rem' }}>|</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#f97316' }}>
                      {stats.totalCompare.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      compares
                    </Typography>
                  </Box>
                </>
              )}
              <Typography color="text.disabled" sx={{ fontSize: '0.75rem' }}>|</Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: stats.successRate !== null && stats.successRate >= 0.95 ? 'success.main' : 'warning.main',
                  }}
                >
                  {stats.successRate !== null ? `${Math.round(stats.successRate * 100)}%` : '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  success
                </Typography>
              </Box>
              <Typography color="text.disabled" sx={{ fontSize: '0.75rem' }}>|</Typography>
              <Tooltip
                title={stats.orgNames.length > 0 ? stats.orgNames.join(', ') : 'No organizations'}
                arrow
              >
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, cursor: 'help' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {stats.uniqueOrgs}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    orgs
                  </Typography>
                </Box>
              </Tooltip>
              <Typography color="text.disabled" sx={{ fontSize: '0.75rem' }}>|</Typography>
              <Tooltip
                title={stats.userNames.length > 0 ? stats.userNames.join(', ') : 'No users'}
                arrow
              >
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, cursor: 'help' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {stats.uniqueUsers}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    users
                  </Typography>
                </Box>
              </Tooltip>
              <Typography color="text.disabled" sx={{ fontSize: '0.75rem' }}>|</Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {stats.avgLatency !== null ? `${stats.avgLatency.toFixed(1)}s` : '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  avg latency
                </Typography>
              </Box>
            </>
          )}
          </Box>
        )}

        {/* Refresh Button */}
        <Button
          size="small"
          onClick={() => fetchRequests()}
          disabled={isLoading}
          startIcon={
            <RefreshIcon
              fontSize="small"
              sx={{
                animation: isLoading ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            />
          }
          sx={{
            color: 'text.secondary',
            textTransform: 'none',
            '&:hover': { color: 'primary.main' },
          }}
        >
          Refresh
        </Button>
      </Box>

      {/* Daily Trends - Collapsible */}
      <Box sx={{ mb: 1.5 }}>
        <Box
          onClick={handleTrendsToggle}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 0.75,
            px: 1.5,
            borderRadius: 1,
            bgcolor: 'rgba(255, 255, 255, 0.02)',
            border: '1px solid',
            borderColor: 'divider',
            cursor: 'pointer',
            userSelect: 'none',
            transition: 'background-color 0.15s',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.04)',
            },
          }}
        >
          <ShowChartIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            Daily Trends
          </Typography>
          <ExpandMoreIcon
            sx={{
              fontSize: 20,
              color: 'text.secondary',
              transition: 'transform 0.3s ease',
              transform: trendsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </Box>

        <Collapse in={trendsExpanded} timeout={300}>
          <Box
            sx={{
              mt: 1,
              p: 2,
              borderRadius: 1,
              bgcolor: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <DailyTrendsChart
              data={trendsData || []}
              isLoading={trendsLoading}
            />
          </Box>
        </Collapse>
      </Box>

      {/* Request List - Scrollable */}
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} variant="rounded" height={100} />
            ))}
          </Box>
        ) : requests.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography color="text.secondary">
              No requests found for {formatRangeLabel()}
            </Typography>
            <Button
              variant="text"
              size="small"
              onClick={goToPreviousDay}
              sx={{ mt: 1 }}
            >
              Check earlier dates
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {requests.map((request) => (
              <Card
                key={request.traceId}
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  },
                }}
                onClick={() => handleRequestClick(request)}
              >
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  {/* Header row with badges */}
                  <Box sx={{ display: 'flex', gap: 0.75, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Chip
                      label={REQUEST_TYPE_LABELS[request.requestType]}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.7rem',
                        bgcolor: REQUEST_TYPE_COLORS[request.requestType] + '25',
                        color: REQUEST_TYPE_COLORS[request.requestType],
                        fontWeight: 600,
                      }}
                    />
                    {request.surface && (
                      <Chip
                        label={surfaceLabel(request.surface)}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: '0.7rem',
                          bgcolor: surfaceColor(request.surface) + '22',
                          color: surfaceColor(request.surface),
                          fontWeight: 600,
                        }}
                      />
                    )}
                    {request.agentName && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {request.agentName}
                      </Typography>
                    )}
                    {request.requestType === 'agent' && (request.turnCount ?? 0) > 1 && (
                      <Tooltip title="Turns in this conversation" arrow>
                        <Chip
                          icon={<ForumIcon sx={{ fontSize: 12 }} />}
                          label={`${request.turnCount} turns`}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            bgcolor: 'rgba(148, 163, 184, 0.15)',
                            color: 'text.secondary',
                            '& .MuiChip-icon': { color: 'text.secondary' },
                          }}
                        />
                      </Tooltip>
                    )}
                    <TurnKindChip kind={request.turnKind} />
                    {request.isFollowup && (
                      <Chip
                        label="FollowUp"
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          bgcolor: 'rgba(59, 130, 246, 0.25)',
                          color: '#3b82f6',
                          fontWeight: 500,
                        }}
                      />
                    )}
                    {request.templateName && (
                      <Chip
                        label="Template"
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          bgcolor: 'rgba(168, 85, 247, 0.25)',
                          color: '#a855f7',
                          fontWeight: 500,
                        }}
                      />
                    )}
                    {request.checklistId && (
                      <Chip
                        label="Checklist"
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          bgcolor: 'rgba(20, 184, 166, 0.25)',
                          color: '#14b8a6',
                          fontWeight: 500,
                        }}
                      />
                    )}
                    {(request.orgName || request.orgId) && (
                      <Chip
                        label={request.orgName || request.orgId?.slice(0, 8) + '...'}
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                    {(request.userName || request.userId) && (
                      <Typography variant="caption" color="text.secondary">
                        {request.userName || request.userId?.slice(0, 8) + '...'}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {format(new Date(request.timestamp), isSingleDay ? 'HH:mm:ss' : 'MMM d, HH:mm')}
                    </Typography>
                    {request.successStatus !== null && (
                      request.successStatus ? (
                        <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16 }} />
                      ) : (
                        <ErrorIcon sx={{ color: 'error.main', fontSize: 16 }} />
                      )
                    )}
                    {request.latency !== null && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                        {request.latency < 1
                          ? `${(request.latency * 1000).toFixed(0)}ms`
                          : `${request.latency.toFixed(1)}s`}
                      </Typography>
                    )}
                    {request.feedbackType && (
                      <Tooltip title={`Feedback: ${request.feedbackType}`} arrow>
                        {request.feedbackType === 'positive' ? (
                          <ThumbUpIcon sx={{ color: 'success.main', fontSize: 14, ml: 0.5 }} />
                        ) : request.feedbackType === 'negative' ? (
                          <ThumbDownIcon sx={{ color: 'error.main', fontSize: 14, ml: 0.5 }} />
                        ) : (
                          <ThumbUpIcon sx={{ color: 'text.disabled', fontSize: 14, ml: 0.5 }} />
                        )}
                      </Tooltip>
                    )}
                    {request.requestType === 'query' && (
                      <Tooltip title="View generated answer" arrow>
                        <Chip
                          icon={<QuestionAnswerIcon sx={{ fontSize: 14 }} />}
                          label="Answer"
                          size="small"
                          clickable
                          onClick={(e) => {
                            e.stopPropagation();
                            openAnswerModal(request.traceId, request.userRequest);
                          }}
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            bgcolor: 'rgba(34, 211, 238, 0.15)',
                            color: '#22d3ee',
                            ml: 0.5,
                            '& .MuiChip-icon': { color: '#22d3ee' },
                            '&:hover': { bgcolor: 'rgba(34, 211, 238, 0.25)' },
                          }}
                        />
                      </Tooltip>
                    )}
                    {request.requestType === 'agent' && (
                      <Tooltip title="View full conversation" arrow>
                        <Chip
                          icon={<ForumIcon sx={{ fontSize: 14 }} />}
                          label="Conversation"
                          size="small"
                          clickable
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(transcriptPath(request.threadId ?? request.traceId));
                          }}
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            bgcolor: 'rgba(139, 92, 246, 0.15)',
                            color: '#a78bfa',
                            ml: 0.5,
                            '& .MuiChip-icon': { color: '#a78bfa' },
                            '&:hover': { bgcolor: 'rgba(139, 92, 246, 0.25)' },
                          }}
                        />
                      </Tooltip>
                    )}
                    {request.pellesAppUrl && (
                      <Tooltip title="View in Pelles App" arrow>
                        <Chip
                          component="a"
                          href={request.pellesAppUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          icon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                          label="View in App"
                          size="small"
                          clickable
                          onClick={(e: React.MouseEvent) => e.stopPropagation()}
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            bgcolor: 'rgba(251, 146, 60, 0.15)',
                            color: '#fb923c',
                            ml: 0.5,
                            '& .MuiChip-icon': { color: '#fb923c' },
                            '&:hover': { bgcolor: 'rgba(251, 146, 60, 0.25)' },
                          }}
                        />
                      </Tooltip>
                    )}
                    {request.userId && (
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          ml: 'auto',
                          pl: 0.75,
                          pr: 0.5,
                          borderRadius: 1,
                          border: '1px solid rgba(183, 148, 244, 0.25)',
                          bgcolor: 'rgba(183, 148, 244, 0.04)',
                          height: 20,
                        }}
                      >
                        <LogRocketIcon size={10} />
                        <Tooltip title="View session replay in LogRocket" arrow>
                          <Chip
                            component="a"
                            href={buildLogRocketUrl(request.userId, request.timestamp)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            label="Session ↗"
                            size="small"
                            clickable
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              bgcolor: 'transparent',
                              color: '#B794F4',
                              '& .MuiChip-label': { px: 0.5 },
                              '&:hover': { bgcolor: 'rgba(183, 148, 244, 0.15)' },
                            }}
                          />
                        </Tooltip>
                        <Box sx={{ width: '1px', height: 12, bgcolor: 'rgba(183, 148, 244, 0.3)' }} />
                        <Tooltip title="Generate AI summary of user session" arrow>
                          <Chip
                            label="✨ AI Summary"
                            size="small"
                            clickable
                            onClick={(e) => {
                              e.stopPropagation();
                              openHighlightsModal(request.userId!, request.timestamp, request.userName || null);
                            }}
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              bgcolor: 'transparent',
                              color: '#B794F4',
                              '& .MuiChip-label': { px: 0.5 },
                              '&:hover': { bgcolor: 'rgba(183, 148, 244, 0.15)' },
                            }}
                          />
                        </Tooltip>
                      </Box>
                    )}
                  </Box>

                  {/* Request text */}
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.85rem',
                      lineHeight: 1.5,
                      bgcolor: 'rgba(0,0,0,0.15)',
                      p: 1,
                      borderRadius: 0.5,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      maxHeight: 80,
                      overflow: 'hidden',
                    }}
                  >
                    {request.userRequestPreview || request.userRequest || 'No request text available'}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}

        {/* Pagination Controls */}
        {!isLoading && requests.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mt: 2,
              pt: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Rows per page:
              </Typography>
              <FormControl size="small" variant="outlined">
                <Select
                  value={pageSize}
                  onChange={handlePageSizeChange as any}
                  sx={{ fontSize: '0.8rem', '& .MuiSelect-select': { py: 0.5 } }}
                >
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={200}>200</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Page {currentPage} of {totalPages} ({totalRequests.toLocaleString()} total)
              </Typography>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="small"
                showFirstButton
                showLastButton
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* Detail Drawer */}
      <ResizableDrawer
        open={isDetailOpen}
        onClose={() => setDetailOpen(false)}
        minWidth={480}
        storageKey="pd-request-detail-width"
      >
        <Box sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Request Details</Typography>
            <IconButton size="small" onClick={() => setDetailOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          {isLoadingDetail ? (
            <Skeleton variant="rounded" height={400} />
          ) : selectedRequest ? (
            <Box>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Chip
                  label={REQUEST_TYPE_LABELS[selectedRequest.requestType]}
                  size="small"
                  sx={{
                    bgcolor: REQUEST_TYPE_COLORS[selectedRequest.requestType] + '25',
                    color: REQUEST_TYPE_COLORS[selectedRequest.requestType],
                  }}
                />
                {selectedRequest.surface && (
                  <Chip
                    label={surfaceLabel(selectedRequest.surface)}
                    size="small"
                    sx={{
                      bgcolor: surfaceColor(selectedRequest.surface) + '22',
                      color: surfaceColor(selectedRequest.surface),
                      fontWeight: 600,
                    }}
                  />
                )}
                {selectedRequest.requestType === 'agent' && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ForumIcon sx={{ fontSize: 14 }} />}
                    onClick={() =>
                      navigate(transcriptPath(selectedRequest.threadId ?? selectedRequest.traceId))
                    }
                    sx={{
                      ml: 'auto',
                      textTransform: 'none',
                      borderColor: '#a78bfa',
                      color: '#a78bfa',
                      '&:hover': { borderColor: '#a78bfa', bgcolor: 'rgba(139, 92, 246, 0.1)' },
                    }}
                  >
                    Open conversation
                  </Button>
                )}
                <TurnKindChip kind={selectedRequest.turnKind} />
                {selectedRequest.isFollowup && (
                  <Chip
                    label="FollowUp"
                    size="small"
                    sx={{
                      bgcolor: 'rgba(59, 130, 246, 0.25)',
                      color: '#3b82f6',
                    }}
                  />
                )}
                {selectedRequest.templateName && (
                  <Chip
                    label={`Template: ${selectedRequest.templateName}`}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(168, 85, 247, 0.25)',
                      color: '#a855f7',
                    }}
                  />
                )}
                <Chip label={selectedRequest.env || 'unknown'} size="small" variant="outlined" />
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Timestamp</Typography>
                  <Typography variant="body2">
                    {format(new Date(selectedRequest.timestamp), 'PPpp')}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Latency</Typography>
                  <Typography variant="body2">
                    {selectedRequest.latency !== null
                      ? selectedRequest.latency < 1
                        ? `${(selectedRequest.latency * 1000).toFixed(0)}ms`
                        : `${selectedRequest.latency.toFixed(2)}s`
                      : 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Organization</Typography>
                  <Typography variant="body2">
                    {selectedRequest.orgName || selectedRequest.orgId || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">User</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2">
                      {selectedRequest.userName || selectedRequest.userId || 'N/A'}
                    </Typography>
                    {(selectedRequest.userName || selectedRequest.userId) && (
                      <Tooltip title="Copy">
                        <IconButton
                          size="small"
                          onClick={() => handleCopy(selectedRequest.userName || selectedRequest.userId || '', 'User')}
                          sx={{ p: 0.25 }}
                        >
                          <ContentCopyIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Project</Typography>
                  <Typography variant="body2">
                    {selectedRequest.projectName || selectedRequest.projectId || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Status</Typography>
                  <Typography variant="body2">
                    {selectedRequest.successStatus === null
                      ? 'Unknown'
                      : selectedRequest.successStatus
                        ? '✓ Success'
                        : '✗ Failed'}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="caption" color="text.secondary">User Request</Typography>
              <Paper
                sx={{
                  p: 1.5,
                  mt: 0.5,
                  bgcolor: 'rgba(0,0,0,0.2)',
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: 250,
                  overflow: 'auto',
                }}
              >
                {selectedRequest.userRequest || 'No request text available'}
              </Paper>

              {selectedRequest.mentions.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Mentions ({selectedRequest.mentions.length})
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                    {selectedRequest.mentions.map((mention, i) => (
                      <Chip key={i} label={mention} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
              )}

              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">Trace ID</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
                    {selectedRequest.traceId}
                  </Typography>
                  <Tooltip title="Copy">
                    <IconButton
                      size="small"
                      onClick={() => handleCopy(selectedRequest.traceId, 'Trace ID')}
                      sx={{ p: 0.25 }}
                    >
                      <ContentCopyIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {selectedRequest.id && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">PelleSmith</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title="Open in PelleSmith">
                      <Box
                        component="a"
                        href={`https://smith.pelles-ai.com/traces?trace_id=${selectedRequest.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          color: '#8b5cf6',
                          textDecoration: 'none',
                          fontSize: '0.875rem',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        View Trace
                        <OpenInNewIcon sx={{ fontSize: 14 }} />
                      </Box>
                    </Tooltip>
                  </Box>
                </Box>
              )}

              {selectedRequest.userId && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">LogRocket</Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      mt: 0.5,
                      px: 0.75,
                      py: 0.5,
                      borderRadius: 1.5,
                      border: '1px solid rgba(183, 148, 244, 0.3)',
                      bgcolor: 'rgba(183, 148, 244, 0.05)',
                    }}
                  >
                    <LogRocketIcon size={14} />
                    <Tooltip title="View session replay in LogRocket" arrow>
                      <Chip
                        component="a"
                        href={buildLogRocketUrl(selectedRequest.userId, selectedRequest.timestamp)}
                        target="_blank"
                        rel="noopener noreferrer"
                        label="Session"
                        size="small"
                        clickable
                        deleteIcon={<OpenInNewIcon sx={{ fontSize: 12 }} />}
                        onDelete={() => {}}
                        sx={{
                          height: 22,
                          bgcolor: 'rgba(183, 148, 244, 0.15)',
                          color: '#B794F4',
                          '& .MuiChip-label': { px: 0.75 },
                          '& .MuiChip-deleteIcon': { color: '#B794F4', mr: 0.25 },
                          '&:hover': { bgcolor: 'rgba(183, 148, 244, 0.25)' },
                        }}
                      />
                    </Tooltip>
                    <Tooltip title="Generate AI summary of user session" arrow>
                      <Chip
                        label="AI Summary"
                        size="small"
                        clickable
                        onClick={() => openHighlightsModal(
                          selectedRequest.userId!,
                          selectedRequest.timestamp,
                          selectedRequest.userName || null
                        )}
                        sx={{
                          height: 22,
                          bgcolor: 'rgba(183, 148, 244, 0.15)',
                          color: '#B794F4',
                          '& .MuiChip-label': { px: 0.75 },
                          '&:hover': { bgcolor: 'rgba(183, 148, 244, 0.25)' },
                        }}
                      />
                    </Tooltip>
                  </Box>
                </Box>
              )}
            </Box>
          ) : null}
        </Box>
      </ResizableDrawer>

      <Snackbar
        open={copySnackbar.open}
        autoHideDuration={2000}
        onClose={() => setCopySnackbar({ ...copySnackbar, open: false })}
        message={copySnackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* LogRocket Highlights Modal */}
      <LogRocketHighlightsModal
        open={highlightsModal.open}
        onClose={closeHighlightsModal}
        userId={highlightsModal.userId}
        timestamp={highlightsModal.timestamp}
        userName={highlightsModal.userName}
      />

      {/* Query Answer Modal */}
      <QueryAnswerModal
        open={answerModal.open}
        onClose={closeAnswerModal}
        traceId={answerModal.traceId}
        question={answerModal.question}
      />

      {/* Agent transcript drawer — the /requests/thread/:threadId child route */}
      <Outlet />
    </Box>
  );
}
